import { useEffect, useRef, useState } from 'react';
import { graphql } from 'react-relay';
import { usePostOfficeSideNavMessages } from '@post-office/placements--growth/confluence-side-nav/contexts/post-office-side-nav-message-provider';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
import type { TreeObjectItems } from '@confluence/tree/entry-points/tree-types.ts';
import { useSpaceId } from '@confluence/space-utils/entry-points/useSpaceId.ts';
import { useSpaceKey } from '@confluence/space-utils/entry-points/useSpaceKey.ts';

import type { ContentTreeItem } from './data-transformers.ts';
import { oneOnOnePagesDetectedEvent } from './analytics.ts';

// GraphQL query to get the current user's personal space key
const useOneOnOnePagesAnalyticsGetCurrentUserPersonalSpaceQuery = graphql`
	query useOneOnOnePagesAnalyticsGetCurrentUserPersonalSpaceQuery {
		user: confluence_atlassianUser(current: true) {
			id
			confluence {
				hasPersonalSpace
				space {
					key
					id
				}
			}
		}
	}
`;

type PersonalSpaceQueryData = {
	user?: {
		confluence?: {
			hasPersonalSpace?: boolean;
			space?: {
				key?: string;
			};
		};
	};
};

// Keywords to detect meeting-related pages
const MEETING_KEYWORDS = [
	'1:1',
	'meeting',
	'<>',
	'weekly',
	'biweekly',
	'ongoing',
	'running',
	'sync',
	'discussion',
	'session',
	'1-1',
];

/**
 * Custom hook to track analytics when 1 or more pages have meeting-related keywords in their title
 * Only fires when the user is in their own personal space
 * @param pages - The current pages from the page tree state
 * @param spaceKey - The current space key
 * @param space - The current space information (for analytics space id)
 */
export const useOneOnOnePagesAnalytics = (pages?: TreeObjectItems<ContentTreeItem>) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const context = usePostOfficeSideNavMessages();
	const hasTrackedRef = useRef(false);
	const [shouldQueryPersonalSpace, setShouldQueryPersonalSpace] = useState(false);
	const spaceKey = useSpaceKey();
	const spaceId = useSpaceId();

	// Only query for personal space info if we detect 1+ meeting pages
	const { data: personalSpaceData } = useQueryInterop<PersonalSpaceQueryData>(
		useOneOnOnePagesAnalyticsGetCurrentUserPersonalSpaceQuery,
		{
			skip: !shouldQueryPersonalSpace || !spaceKey,
		},
	);

	useEffect(() => {
		if (!pages || Object.keys(pages).length === 0 || !spaceKey || !context) {
			return;
		}

		const oneOnOnePages = (Object.values(pages) as ContentTreeItem[]).filter((page) => {
			const title = page.data?.title?.toLowerCase() || '';
			const fullTitle = page.data?.fullTitle?.toLowerCase() || '';

			return MEETING_KEYWORDS.some(
				(keyword) => title.includes(keyword) || fullTitle.includes(keyword),
			);
		});

		// First check if we have 1+ meeting pages
		if (oneOnOnePages.length >= 1) {
			// Enable the personal space query if not already done
			if (!shouldQueryPersonalSpace) {
				setShouldQueryPersonalSpace(true);
				return; // Wait for query to complete
			}

			// Now check if we're in the user's personal space
			const userPersonalSpaceKey = personalSpaceData?.user?.confluence?.space?.key;

			// Only fire analytics if current space is the user's personal space
			if (userPersonalSpaceKey && spaceKey === userPersonalSpaceKey && !hasTrackedRef.current) {
				createAnalyticsEvent(
					oneOnOnePagesDetectedEvent(oneOnOnePages.length, Object.keys(pages).length, spaceId),
				).fire();

				// Enable post office side nav message for 1:1 pages detected
				context.enablePostOfficeSideNavMessage('1_ON_1_LOOM_NUDGE');

				hasTrackedRef.current = true;
			}
		}
	}, [
		pages,
		createAnalyticsEvent,
		spaceId,
		spaceKey,
		personalSpaceData,
		shouldQueryPersonalSpace,
		context,
	]);
};
